import { usePermissions } from '../../core/authorization/PermissionProvider';
import React, { useEffect, useMemo, useState } from 'react';
import './RoleActionsPage.css';
import BasicCli from '../../main/components/basicCli/BasicCli';
import axiosInstance from '../../main/initializers/axios';
import { ApiConstants } from '../../main/constants/ApiConstants';
import useGetUser from '../../main/hooks/useGetUser';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

type CompanyOption = {
  id: string;
  name: string;
};

// Platform ids are GUIDs; `companyId` is null for a system role (PLATFORM_ADMIN).
type RoleOption = {
  id: string;
  code: string;
  name: string;
  companyId: string | null;
  isSystem: boolean;
  isFullAccess: boolean;
};

type ProvisionForm = {
  name: string;
  code: string;
  location: string;
  email: string;
  phone: string;
  website: string;
  registrationNumber: string;
  workingHours: string;
  timeZone: string;
  description: string;
};

type ProvisionResponse = {
  companyId: string;
  companyName: string;
  adminRoleId: string;
  adminUserId: string;
  adminEmail: string;
  adminPassword: string;
};

const initialForm: ProvisionForm = {
  name: '',
  code: '',
  location: '',
  email: '',
  phone: '',
  website: '',
  registrationNumber: '',
  workingHours: '',
  timeZone: 'Europe/Tirane',
  description: '',
};

const RoleActionsPage: React.FC = () => {
  const { can, hasScope } = usePermissions();
  const currentUser = useGetUser();
  const isSuperAdmin = (currentUser?.role?.code ?? '').toLowerCase() === 'superadmin';
  const { i18n } = useTranslation();

  const [roles, setRoles] = useState<RoleOption[]>([]);
  const [companies, setCompanies] = useState<CompanyOption[]>([]);
  const [sourceCompanyId, setSourceCompanyId] = useState('');
  const [targetCompanyId, setTargetCompanyId] = useState('');
  const [transferSourceRoleId, setTransferSourceRoleId] = useState('');
  const [transferTargetRoleId, setTransferTargetRoleId] = useState('');
  const [isTransfering, setIsTransfering] = useState(false);
  const [isProvisioning, setIsProvisioning] = useState(false);
  const [form, setForm] = useState<ProvisionForm>(initialForm);
  const [lastProvision, setLastProvision] = useState<ProvisionResponse | null>(null);

  const loadData = async () => {
    try {
      const [rolesRes, companiesRes] = await Promise.all([
        axiosInstance.get(ApiConstants.Roles.list, { params: { scope: 'all' } }),
        axiosInstance.get(ApiConstants.Company.list, { params: { scope: 'all' } }),
      ]);

      setRoles(rolesRes.data ?? []);
      setCompanies(companiesRes.data ?? []);
    } catch {
      toast.error(i18n.t('roleActions.loadError', { defaultValue: 'Failed to load actions data.' }));
    }
  };

  useEffect(() => {
    if (isSuperAdmin) {
      void loadData();
    }
  }, [isSuperAdmin]);

  // A system role (PLATFORM_ADMIN) is a valid source for any company.
  const sourceRoles = useMemo(() => {
    if (!sourceCompanyId) return roles;
    return roles.filter((role) => role.companyId === sourceCompanyId || role.companyId === null);
  }, [roles, sourceCompanyId]);

  // System and full-access roles (PLATFORM_ADMIN, each company's ADMIN) cannot receive grants.
  const targetRoles = useMemo(() => {
    const assignable = roles.filter((role) => role.companyId !== null && !role.isSystem && !role.isFullAccess);
    if (!targetCompanyId) return assignable;
    return assignable.filter((role) => role.companyId === targetCompanyId);
  }, [roles, targetCompanyId]);

  const roleLabel = (role: RoleOption) => {
    const owner = role.companyId === null
      ? i18n.t('roleActions.system', { defaultValue: 'System' })
      : companies.find((company) => company.id === role.companyId)?.name ?? role.companyId;
    return `${role.code} (${owner})`;
  };

  const onChangeForm = (field: keyof ProvisionForm, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleProvision = async () => {
    if (!hasScope("platform.organization.provision")) return;
    if (!form.name.trim() || !form.code.trim()) {
      toast.error(i18n.t('roleActions.companyNameCodeRequired', { defaultValue: 'Company name and code are required.' }));
      return;
    }

    setIsProvisioning(true);
    try {
      const response = await axiosInstance.post<ProvisionResponse>(ApiConstants.Company.provision, {
        name: form.name.trim(),
        code: form.code.trim(),
        location: form.location.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        website: form.website.trim(),
        registrationNumber: form.registrationNumber.trim(),
        workingHours: form.workingHours.trim(),
        timeZone: form.timeZone.trim() || 'Europe/Tirane',
        description: form.description.trim(),
        isActive: true,
        isVatRegistered: false,
        defaultVatRate: 0,
      });

      setLastProvision(response.data);
      setTargetCompanyId(response.data.companyId);
      setTransferTargetRoleId('');
      setForm(initialForm);
      await loadData();
      toast.success(
        i18n.t('roleActions.companyCreated', {
          defaultValue: 'Company {{name}} created.',
          name: response.data.companyName,
        }),
      );
    } catch {
      toast.error(i18n.t('roleActions.createCompanyError', { defaultValue: 'Failed to create company.' }));
    } finally {
      setIsProvisioning(false);
    }
  };

  const handleTransfer = async () => {
    if (!can("platform.roles", "execute")) return;
    if (!transferSourceRoleId || !transferTargetRoleId) {
      toast.error(i18n.t('roleActions.selectSourceTargetRole', { defaultValue: 'Select source and target role.' }));
      return;
    }

    if (transferSourceRoleId === transferTargetRoleId) {
      toast.error(i18n.t('roleActions.rolesMustBeDifferent', { defaultValue: 'Source and target roles must be different.' }));
      return;
    }

    const sourceRole = roles.find((role) => role.id === transferSourceRoleId);
    const targetRole = roles.find((role) => role.id === transferTargetRoleId);

    if (!sourceRole || !targetRole) {
      toast.error(i18n.t('roleActions.invalidRolesSelected', { defaultValue: 'Invalid roles selected.' }));
      return;
    }

    setIsTransfering(true);
    try {
      await axiosInstance.post(ApiConstants.Roles.transfer, {
        roleSId: transferSourceRoleId,
        roleTId: transferTargetRoleId,
        source: sourceRole.name,
        target: targetRole.name,
      });

      toast.success(i18n.t('roleActions.transferSuccess', { defaultValue: 'Role grants transferred successfully.' }));
    } catch {
      toast.error(i18n.t('roleActions.transferError', { defaultValue: 'Failed to transfer grants.' }));
    } finally {
      setIsTransfering(false);
    }
  };

  if (!isSuperAdmin) {
    return (
      <BasicCli>
        <div className="raWrap">
          <div className="raContainer">
            <div className="bcCard raCard">
              <h1 className="raTitle">{i18n.t('roleActions.title', { defaultValue: 'Role Actions' })}</h1>
              <p className="raMuted">{i18n.t('roleActions.superAdminOnly', { defaultValue: 'This page is available only for SuperAdmin users.' })}</p>
            </div>
          </div>
        </div>
      </BasicCli>
    );
  }

  return (
    <BasicCli>
      <div className="raWrap">
        <div className="raContainer">
          <div className="bcCard raCard">
            <div className="raCardHeader">
              <div>
                <h1 className="raTitle">{i18n.t('roleActions.title', { defaultValue: 'Role Actions' })}</h1>
                <p className="raMuted">
                  {i18n.t('roleActions.subtitle', {
                    defaultValue: 'Used by SuperAdmin to create new companies and transfer grants to company admins.',
                  })}
                </p>
              </div>
            </div>

            <div className="raGrid">
              <div className="raPanel">
                <h2 className="raSectionTitle">{i18n.t('roleActions.createNewCompany', { defaultValue: 'Create New Company' })}</h2>
                <div className="raFormGrid">
                  <label className="raField">
                    <span>{i18n.t('roleActions.companyName', { defaultValue: 'Company Name' })}</span>
                    <input className="ui-control ui-input" value={form.name} onChange={(e) => onChangeForm('name', e.target.value)} />
                  </label>

                  <label className="raField">
                    <span>{i18n.t('roleActions.companyCode', { defaultValue: 'Company Code' })}</span>
                    <input className="ui-control ui-input" value={form.code} onChange={(e) => onChangeForm('code', e.target.value)} />
                  </label>

                  <label className="raField">
                    <span>{i18n.t('generic.email', { defaultValue: 'Email' })}</span>
                    <input className="ui-control ui-input" value={form.email} onChange={(e) => onChangeForm('email', e.target.value)} />
                  </label>

                  <label className="raField">
                    <span>{i18n.t('company.phone', { defaultValue: 'Phone' })}</span>
                    <input className="ui-control ui-input" value={form.phone} onChange={(e) => onChangeForm('phone', e.target.value)} />
                  </label>

                  <label className="raField">
                    <span>{i18n.t('roleActions.website', { defaultValue: 'Website' })}</span>
                    <input className="ui-control ui-input" value={form.website} onChange={(e) => onChangeForm('website', e.target.value)} />
                  </label>

                  <label className="raField">
                    <span>{i18n.t('company.registrationNumber', { defaultValue: 'Registration Number' })}</span>
                    <input className="ui-control ui-input" value={form.registrationNumber} onChange={(e) => onChangeForm('registrationNumber', e.target.value)} />
                  </label>

                  <label className="raField">
                    <span>{i18n.t('company.location', { defaultValue: 'Location' })}</span>
                    <input className="ui-control ui-input" value={form.location} onChange={(e) => onChangeForm('location', e.target.value)} />
                  </label>

                  <label className="raField">
                    <span>{i18n.t('company.workingHours', { defaultValue: 'Working Hours' })}</span>
                    <input className="ui-control ui-input" value={form.workingHours} onChange={(e) => onChangeForm('workingHours', e.target.value)} />
                  </label>

                  <label className="raField">
                    <span>{i18n.t('roleActions.timeZone', { defaultValue: 'Time Zone' })}</span>
                    <input className="ui-control ui-input" value={form.timeZone} onChange={(e) => onChangeForm('timeZone', e.target.value)} />
                  </label>

                  <label className="raField raFieldFull">
                    <span>{i18n.t('company.description', { defaultValue: 'Description' })}</span>
                    <textarea className="ui-control ui-input raTextarea" value={form.description} onChange={(e) => onChangeForm('description', e.target.value)} />
                  </label>
                </div>

                <div className="raActionRow">
                  <button className="ui-control ui-btn ui-btn--primary" type="button" onClick={handleProvision} disabled={isProvisioning || !hasScope("platform.organization.provision")}>
                    {isProvisioning
                      ? i18n.t('roleActions.creating', { defaultValue: 'Creating...' })
                      : i18n.t('roleActions.createCompany', { defaultValue: 'Create Company' })}
                  </button>
                </div>

                {lastProvision && (
                  <div className="raResult">
                    <div className="raResultTitle">{i18n.t('roleActions.bootstrapCreated', { defaultValue: 'Bootstrap created' })}</div>
                    <div>
                      {i18n.t('roleActions.companyLabel', { defaultValue: 'Company' })}: {lastProvision.companyName}
                    </div>
                    <div>
                      {i18n.t('roleActions.adminEmail', { defaultValue: 'Admin email' })}: {lastProvision.adminEmail}
                    </div>
                    <div>
                      {i18n.t('roleActions.password', { defaultValue: 'Password' })}: {lastProvision.adminPassword}
                    </div>
                  </div>
                )}
              </div>

              <div className="raPanel">
                <h2 className="raSectionTitle">{i18n.t('roleActions.transferGrants', { defaultValue: 'Transfer Grants' })}</h2>

                <label className="raField">
                  <span>{i18n.t('roleActions.sourceCompany', { defaultValue: 'Source Company' })}</span>
                  <select className="ui-control ui-input" value={sourceCompanyId} onChange={(e) => setSourceCompanyId(e.target.value)}>
                    <option value="">{i18n.t('roleActions.allCompanies', { defaultValue: 'All companies' })}</option>
                    {companies.map((company) => (
                      <option key={`src-company-${company.id}`} value={company.id}>
                        {company.name}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="raField">
                  <span>{i18n.t('roleActions.sourceRole', { defaultValue: 'Source Role' })}</span>
                  <select className="ui-control ui-input" value={transferSourceRoleId} onChange={(e) => setTransferSourceRoleId(e.target.value)}>
                    <option value="">{i18n.t('roleActions.selectSourceRole', { defaultValue: 'Select source role' })}</option>
                    {sourceRoles.map((role) => (
                      <option key={`src-role-${role.id}`} value={role.id}>
                        {roleLabel(role)}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="raField">
                  <span>{i18n.t('roleActions.targetCompany', { defaultValue: 'Target Company' })}</span>
                  <select className="ui-control ui-input" value={targetCompanyId} onChange={(e) => setTargetCompanyId(e.target.value)}>
                    <option value="">{i18n.t('roleActions.allCompanies', { defaultValue: 'All companies' })}</option>
                    {companies.map((company) => (
                      <option key={`target-company-${company.id}`} value={company.id}>
                        {company.name}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="raField">
                  <span>{i18n.t('roleActions.targetRole', { defaultValue: 'Target Role' })}</span>
                  <select className="ui-control ui-input" value={transferTargetRoleId} onChange={(e) => setTransferTargetRoleId(e.target.value)}>
                    <option value="">{i18n.t('roleActions.selectTargetRole', { defaultValue: 'Select target role' })}</option>
                    {targetRoles.map((role) => (
                      <option key={`target-role-${role.id}`} value={role.id}>
                        {roleLabel(role)}
                      </option>
                    ))}
                  </select>
                </label>

                <div className="raHint">
                  {i18n.t('roleActions.transferHint', {
                    defaultValue: "A company's ADMIN role already has full access. Create a role in the target company, then copy grants into it.",
                  })}
                </div>

                <div className="raActionRow">
                  <button className="ui-control ui-btn ui-btn--primary" type="button" onClick={handleTransfer} disabled={isTransfering || !can("platform.roles", "execute")}>
                    {isTransfering
                      ? i18n.t('roleActions.transferring', { defaultValue: 'Transferring...' })
                      : i18n.t('roleActions.transferGrants', { defaultValue: 'Transfer Grants' })}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </BasicCli>
  );
};

export default RoleActionsPage;
